import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useProgress } from "@react-three/drei";

interface Props {
  onComplete: () => void;
}

const buildLines = (
  cores: number,
  mem: string,
  gpu: string,
  pct: number,
): string[] => {
  const lines = [
    "POST [ok]",
    `CPU: detected · ${cores}c · ${mem}gb`,
    `GPU: ${gpu}`,
    `loading assets/room.glb            [${pct}%]`,
    `decoding ktx2 textures             [${pct}%]`,
    `compiling shaders                  [${pct}%]`,
    `mounting adam@kotlorz              [${pct}%]`,
  ];
  if (pct >= 100) lines.push("ready.");
  return lines;
};

const detectGpu = (): string => {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2") ?? canvas.getContext("webgl");
    if (!gl) return "no webgl";
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    if (dbg)
      return (gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) as string).slice(
        0,
        32,
      );
    return "webgl detected";
  } catch {
    return "webgl detected";
  }
};

export const BootSequence = ({ onComplete }: Props): JSX.Element => {
  const { progress } = useProgress();
  const pct = Math.round(progress);
  const cores = navigator.hardwareConcurrency || 4;
  const memVal = (navigator as Navigator & { deviceMemory?: number })
    .deviceMemory;
  const mem = memVal !== undefined ? String(memVal) : "?";
  const [gpu] = useState<string>(() => detectGpu());

  const [revealed, setRevealed] = useState<number>(0);
  const allLines = buildLines(cores, mem, gpu, pct);

  useEffect(() => {
    if (revealed >= allLines.length) return;
    const id = window.setTimeout(() => {
      setRevealed((n) => Math.min(n + 1, allLines.length));
    }, 30);
    return () => window.clearTimeout(id);
  }, [revealed, allLines.length]);

  useEffect(() => {
    if (pct >= 100 && revealed >= allLines.length) {
      const t = window.setTimeout(onComplete, 600);
      return () => window.clearTimeout(t);
    }
    return;
  }, [pct, revealed, allLines.length, onComplete]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="absolute inset-0 z-50 flex items-center justify-center bg-bg0/80 backdrop-blur-sm"
    >
      <div className="w-[480px] max-w-[90vw] border border-rule bg-bg1 p-6 font-mono text-sm text-phosphor">
        {allLines.slice(0, revealed).map((l, i) => (
          <div key={i}>{l}</div>
        ))}
      </div>
    </div>
  );
};
